import { getAskThreadMessages } from "src/store/slices/messageSlice";
import { mapAnonymousThreads } from "src/store/slices/onboardingSlice";
import { getAllThreads } from "src/store/slices/threadsSlice";
import { resolveAnonymousThreadIds } from "src/utils/anonymousThreadId";
import { EVENT_NAMES, EVENT_PARAMETERS, getPageName } from "src/utils/EventConstants";
import { trackAnalytics } from "src/pages/helpers";

/**
 * Ownership of anonymously-created threads transfers through exactly one
 * backend operation: `POST /thread/map-anonymous`. Nothing used to sequence
 * that call ahead of the thread fetch, so `list-messages` regularly won the
 * race and the backend correctly answered `invalid_thread_owner` for a thread
 * the user was about to legitimately own.
 *
 * This module is the ordering primitive. See design.md D2/D4.
 *
 * INH-5375 widened it from 1:1 to 1:N — one call now maps every stored ID, so
 * the gate asks "has the mapping covering this thread settled?" rather than
 * tracking a remap for a single target.
 */

/**
 * Fall-through cap. Above the mapping call's typical latency, below the point a
 * chat load reads as broken. A hung or never-dispatched mapping must never be
 * able to deadlock the chat page.
 *
 * With no cap on how many IDs a batch can carry, confirm this against backend
 * latency for large batches before trusting it (INH-5360).
 */
export const OWNERSHIP_REMAP_MAX_WAIT_MS = 4000;

export type RemapOutcome = "not_needed" | "success" | "failed" | "timeout";

export interface ThreadOwnershipReadiness {
  /** True only when the fetch was actually held back. */
  deferred: boolean;
  waitMs: number;
  remapOutcome: RemapOutcome;
}

type Dispatch = (action: any) => any; // eslint-disable-line @typescript-eslint/no-explicit-any
type GetState = () => any; // eslint-disable-line @typescript-eslint/no-explicit-any

/**
 * Both thunks live in `.js` slices, so TS infers their thunk-argument type as
 * `void`. Narrow them once here at the JS boundary rather than casting at each
 * call site — the slices get real types when they are next substantially
 * edited, per the repo's gradual-migration language policy.
 */
type ArgThunk<A> = (arg: A) => any; // eslint-disable-line @typescript-eslint/no-explicit-any
const dispatchMapThunk = mapAnonymousThreads as unknown as ArgThunk<{ threadIds: string[] }>;
const fetchThreadMessages = getAskThreadMessages as unknown as ArgThunk<{
  threadId: string;
  dispatchSource: string;
}>;

interface GateArgs {
  threadId?: string | null;
  dispatch: Dispatch;
  getState: GetState;
}

/**
 * The single in-flight mapping call, or null when none is open.
 *
 * Was a Map keyed by thread ID when each thread needed its own request. One
 * call now covers every ID, so a single promise is the whole registry — but the
 * dedupe property it existed for still matters: two dispatch sites racing (the
 * `/try` redirect and the chat page mounting a tick later) must not produce two
 * requests.
 */
let inFlightMap: Promise<"success" | "failed"> | null = null;

/**
 * Dispatch the mapping call for `threadIds`, or hand back the one already in
 * flight. Every dispatch site goes through here.
 */
export const dispatchThreadMap = ({
  threadIds,
  dispatch,
}: {
  threadIds: string[];
  dispatch: Dispatch;
}): Promise<"success" | "failed"> => {
  if (inFlightMap) {
    return inFlightMap;
  }
  if (!threadIds || threadIds.length === 0) {
    return Promise.resolve("failed");
  }

  const promise = (async (): Promise<"success" | "failed"> => {
    try {
      const action = await dispatch(dispatchMapThunk({ threadIds }));
      // The thunk returns null (sending nothing) when no access token is
      // present, so a fulfilled action alone is not proof the call went out.
      const sent = action?.meta?.requestStatus === "fulfilled" && Boolean(action?.payload?.result);
      return sent ? "success" : "failed";
    } catch {
      return "failed";
    } finally {
      inFlightMap = null;
    }
  })();
  inFlightMap = promise;
  return promise;
};

/**
 * Hold a thread fetch until the anonymous-thread mapping covering that thread
 * has reached a terminal outcome. Resolves immediately in the ordinary
 * signed-in case — there are no anonymous IDs to map — so normal navigation is
 * unaffected.
 */
export const awaitThreadOwnershipReady = async ({
  threadId,
  dispatch,
  getState,
}: GateArgs): Promise<ThreadOwnershipReadiness> => {
  const notDeferred = (remapOutcome: RemapOutcome): ThreadOwnershipReadiness => ({
    deferred: false,
    waitMs: 0,
    remapOutcome,
  });

  if (!threadId) {
    return notDeferred("not_needed");
  }

  const anonymousThreadIds = resolveAnonymousThreadIds();
  if (anonymousThreadIds.length === 0) {
    return notDeferred("not_needed");
  }

  if (!anonymousThreadIds.includes(threadId)) {
    // The thread being opened is not one of the stored anonymous threads, so
    // this fetch cannot hit an ownership failure the mapping would fix. Still
    // map the stored IDs — that is the recovery path — but do not hold up this
    // fetch.
    dispatchThreadMap({ threadIds: anonymousThreadIds, dispatch });
    return notDeferred("not_needed");
  }

  if (getState()?.onboarding?.anonymousMapStatus === "settled") {
    // Already settled (e.g. PostAuth got there first) — nothing to wait for.
    return notDeferred("success");
  }

  const startedAt = Date.now();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const outcome = await Promise.race<RemapOutcome>([
    dispatchThreadMap({ threadIds: anonymousThreadIds, dispatch }),
    new Promise<RemapOutcome>((resolve) => {
      timer = setTimeout(() => resolve("timeout"), OWNERSHIP_REMAP_MAX_WAIT_MS);
    }),
  ]);
  if (timer) {
    clearTimeout(timer);
  }

  const readiness: ThreadOwnershipReadiness = {
    deferred: true,
    waitMs: Date.now() - startedAt,
    remapOutcome: outcome,
  };

  trackAnalytics(EVENT_NAMES.DEFERRED_THREAD_OWNERSHIP_CHECK, {
    [EVENT_PARAMETERS.THREAD_ID]: threadId,
    [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
    [EVENT_PARAMETERS.WAIT_MS]: readiness.waitMs,
    [EVENT_PARAMETERS.REMAP_OUTCOME]: readiness.remapOutcome,
    [EVENT_PARAMETERS.BATCH_SIZE]: anonymousThreadIds.length,
  });

  return readiness;
};

/**
 * Post-mapping refetch. Extracted from `WebsocketProvider.recoverAnonymousThreads`
 * so the WS recovery path and the ownership retry hook share one implementation
 * rather than drifting into two copies of the same three dispatches.
 */
export const applyRemapSuccess = ({
  threadId,
  dispatch,
  getState,
}: {
  threadId: string;
  dispatch: Dispatch;
  getState: GetState;
}): void => {
  dispatch(getAllThreads());
  const activeThreadId = getState()?.messageSlice?.threadData?.id;
  if (activeThreadId === threadId) {
    // The user is looking at the orphaned thread — refetch so the unauthorized
    // state clears without a manual reload.
    dispatch(fetchThreadMessages({ threadId, dispatchSource: "anonThreadRecovery" }));
  }
};

/** Test-only: drop any in-flight mapping bookkeeping between cases. */
export const __resetRemapRegistryForTests = (): void => {
  inFlightMap = null;
};
